import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Checkbox,
  FormControlLabel,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FolderOpen as ProjectIcon,
  Close as CloseIcon,
  Block as BlockIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { projectsAPI, customersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customer_id: '',
  });

  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { user } = useAuth();

  useEffect(() => {
    fetchProjects();
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const fetchProjects = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await projectsAPI.getAll(showInactive);
      setProjects(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Projekte');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await customersAPI.getAll();
      setCustomers(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Kunden:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingId) {
        await projectsAPI.update(editingId, formData);
        setSuccess('Projekt erfolgreich aktualisiert');
      } else {
        await projectsAPI.create(formData);
        setSuccess('Projekt erfolgreich erstellt');
      }

      resetForm();
      fetchProjects();
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern des Projekts');
    }
  };

  const handleEdit = (project) => {
    setFormData({
      name: project.name,
      description: project.description || '',
      customer_id: project.customer_id || '',
    });
    setEditingId(project.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie dieses Projekt wirklich deaktivieren? Zugehörige Tasks und Zeiteinträge bleiben erhalten, sind aber nicht mehr sichtbar.')) {
      return;
    }

    try {
      await projectsAPI.delete(id);
      setSuccess('Projekt erfolgreich deaktiviert');
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Deaktivieren des Projekts');
    }
  };

  const handleReactivate = async (id) => {
    try {
      await projectsAPI.update(id, { is_active: true });
      setSuccess('Projekt erfolgreich reaktiviert');
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Reaktivieren des Projekts');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      customer_id: '',
    });
    setEditingId(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (user.role !== 'admin') {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <BlockIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: '#e01e26' }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Zugriff verweigert
              </Typography>
              <Typography variant="body1" color="error.main">
                Sie haben keine Berechtigung, auf diese Seite zuzugreifen. Admin-Rechte erforderlich.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>
          Projekte
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateModal}
          sx={{
            background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
            color: 'white',
            fontWeight: 600,
            px: 3,
            py: 1,
            '&:hover': {
              background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
            },
          }}
        >
          Projekt erstellen
        </Button>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Projects Table */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight={600}>
              Alle Projekte
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  sx={{
                    color: '#e01e26',
                    '&.Mui-checked': {
                      color: '#e01e26',
                    },
                  }}
                />
              }
              label="Inaktive anzeigen"
            />
          </Box>

          {projects.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <ProjectIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" gutterBottom>
                Keine Projekte vorhanden
              </Typography>
              <Typography variant="body2">
                Erstellen Sie Ihr erstes Projekt!
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Projekt Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Kunde</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projects.map((project) => (
                    <TableRow
                      key={project.id}
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {project.name}
                          </Typography>
                          {!project.is_active && (
                            <Chip
                              label="Inaktiv"
                              size="small"
                              sx={{
                                bgcolor: '#f5f5f5',
                                color: '#616161',
                                fontSize: '0.7rem',
                                height: '20px',
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {project.customer_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {project.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {project.is_active ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(project)}
                                sx={{
                                  color: '#1e3a5f',
                                  '&:hover': { bgcolor: 'primary.light' },
                                }}
                                title="Bearbeiten"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(project.id)}
                                sx={{
                                  color: '#e01e26',
                                  '&:hover': { bgcolor: 'error.light' },
                                }}
                                title="Deaktivieren"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => handleReactivate(project.id)}
                              sx={{
                                color: '#10b981',
                                '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)' },
                              }}
                              title="Reaktivieren"
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog
        open={showModal}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {editingId ? 'Projekt bearbeiten' : 'Projekt erstellen'}
          </Typography>
          <IconButton onClick={closeModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="project-form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Projekt Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Kunde</InputLabel>
              <Select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                label="Kunde"
              >
                <MenuItem value="">
                  <em>Kein Kunde zugeordnet</em>
                </MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              margin="normal"
              label="Beschreibung"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeModal}
            sx={{
              color: '#1e3a5f',
              fontWeight: 600,
            }}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            form="project-form"
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
              },
            }}
          >
            {editingId ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectsPage;
